import { AUTH0_DOMAIN, AUTH0_DOCTOR_CLIENT_ID, AUTH0_PARENT_CLIENT_ID } from '@/lib/auth0-config';

// Utility: Hard logout (federated) and cleanup
function hardLogout(userType: 'doctor' | 'parent') {
  // Only redirect to Auth0 logout endpoint for the correct app
  const clientId = userType === 'doctor' ? AUTH0_DOCTOR_CLIENT_ID : AUTH0_PARENT_CLIENT_ID;
  const loginRoute = userType === 'doctor' ? '/doctor/login' : '/parent/login';
  const logoutUrl = `https://${AUTH0_DOMAIN}/v2/logout?client_id=${clientId}&returnTo=${encodeURIComponent(globalThis.location.origin + loginRoute)}&federated`;
  globalThis.location.href = logoutUrl;
}
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';

/**
 * Loading spinner component for auth callbacks
 */
function AuthLoadingSpinner() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
        <p className="text-gray-600">{t('auth.verifyingAuth')}</p>
      </div>
    </div>
  );
}

/**
 * Handles the Auth0 callback redirect for Doctor login.
 * Gets the access token from Auth0 and stores it for API calls.
 */
export function DoctorAuthCallback() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user, getAccessTokenSilently } = useAuth0();
  const { setUserType } = useAuthStore();
  const [tokenFetched, setTokenFetched] = useState(false);

  useEffect(() => {
    if (isLoading) return;

    if (isAuthenticated && user) {
      const fetchTokenAndRedirect = async () => {
        try {
          console.log('[DoctorAuthCallback] Fetching access token for:', user.email);
          const accessToken = await getAccessTokenSilently();
          console.log('[DoctorAuthCallback] Access token received, length:', accessToken.length);
          
          // Success: store token and proceed
          localStorage.setItem('auth_token', accessToken);
          localStorage.setItem('userType', 'doctor');
          localStorage.setItem('userType_doctor', 'doctor');
          setUserType('doctor');
          
          console.log('[DoctorAuthCallback] Auth state stored, navigating to dashboard');
          
          // Small delay to ensure storage is persisted
          await new Promise(resolve => setTimeout(resolve, 100));
          
          navigate('/doctor/dashboard', { replace: true });
        } catch (error) {
          // If error (could be rejected by Auth0 Action), force hard logout
          console.error('[DoctorAuthCallback] Auth error:', error);
          hardLogout('doctor');
        }
      };

      if (!tokenFetched) {
        setTokenFetched(true);
        fetchTokenAndRedirect();
      }
    } else if (!isLoading && !isAuthenticated) {
      // Not authenticated - could be rejected by Auth0 Action
      // Force hard logout to clear any stale session
      console.warn('[DoctorAuthCallback] Not authenticated after callback');
      hardLogout('doctor');
    }
  }, [isAuthenticated, isLoading, user, navigate, setUserType, getAccessTokenSilently, tokenFetched]);

  return <AuthLoadingSpinner />;
}

/**
 * Handles the Auth0 callback redirect for Parent login.
 * Gets the access token from Auth0 and stores it for API calls.
 */
export function ParentAuthCallback() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user, getAccessTokenSilently } = useAuth0();
  const { setUserType } = useAuthStore();
  const [tokenFetched, setTokenFetched] = useState(false);

  useEffect(() => {
    if (isLoading) return;

    if (isAuthenticated && user) {
      const fetchTokenAndRedirect = async () => {
        try {
          console.log('[ParentAuthCallback] Fetching access token for:', user.email);
          const accessToken = await getAccessTokenSilently();
          console.log('[ParentAuthCallback] Access token received, length:', accessToken.length);
          
          // Success: store token and proceed
          localStorage.setItem('auth_token', accessToken);
          localStorage.setItem('userType', 'parent');
          localStorage.setItem('userType_parent', 'parent');
          setUserType('parent');
          
          console.log('[ParentAuthCallback] Auth state stored, navigating to dashboard');
          
          // Small delay to ensure storage is persisted
          await new Promise(resolve => setTimeout(resolve, 100));
          
          navigate('/parent/dashboard', { replace: true });
        } catch (error) {
          // If error (could be rejected by Auth0 Action), force hard logout
          console.error('[ParentAuthCallback] Auth error:', error);
          hardLogout('parent');
        }
      };

      if (!tokenFetched) {
        setTokenFetched(true);
        fetchTokenAndRedirect();
      }
    } else if (!isLoading && !isAuthenticated) {
      // Not authenticated - could be rejected by Auth0 Action
      // Force hard logout to clear any stale session
      console.warn('[ParentAuthCallback] Not authenticated after callback');
      hardLogout('parent');
    }
  }, [isAuthenticated, isLoading, user, navigate, setUserType, getAccessTokenSilently, tokenFetched]);

  return <AuthLoadingSpinner />;
}

/**
 * Legacy AuthCallback - redirects based on userType in localStorage
 * @deprecated Use DoctorAuthCallback or ParentAuthCallback instead
 */
export function AuthCallback() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user, getAccessTokenSilently } = useAuth0();
  const { setUserType } = useAuthStore();
  const [tokenFetched, setTokenFetched] = useState(false);

  useEffect(() => {
    // Wait for Auth0 to finish loading
    if (isLoading) return;

    // If authenticated, get the access token and redirect to the correct dashboard
    if (isAuthenticated && user) {
      const fetchTokenAndRedirect = async () => {
        try {
          // Get the access token from Auth0
          const accessToken = await getAccessTokenSilently();
          
          // Store the token for API calls
          localStorage.setItem('auth_token', accessToken);
          
          const userType = localStorage.getItem('userType') as 'doctor' | 'parent';
          
          if (userType) {
            setUserType(userType);
            const redirectPath = userType === 'doctor' ? '/doctor/dashboard' : '/parent/dashboard';
            navigate(redirectPath, { replace: true });
          } else {
            // Default to parent if no userType stored
            setUserType('parent');
            navigate('/parent/dashboard', { replace: true });
          }
        } catch (error) {
          console.error('Error getting access token:', error);
          // Still try to redirect even if token fetch fails
          const userType = localStorage.getItem('userType') as 'doctor' | 'parent';
          if (userType) {
            setUserType(userType);
            navigate(userType === 'doctor' ? '/doctor/dashboard' : '/parent/dashboard', { replace: true });
          } else {
            navigate('/parent/login', { replace: true });
          }
        }
      };

      if (!tokenFetched) {
        setTokenFetched(true);
        fetchTokenAndRedirect();
      }
    } else if (!isLoading && !isAuthenticated) {
      // Not authenticated, go to parent login
      navigate('/parent/login', { replace: true });
    }
  }, [isAuthenticated, isLoading, user, navigate, setUserType, getAccessTokenSilently, tokenFetched]);

  return <AuthLoadingSpinner />;
}
